import { CONTACT_EMAIL, DIVISIONS, SPONSOR, SITE_NAME, SUBSTACK_URL, TAGLINE } from "./site";

/**
 * Every link on this page leaves for the apps or the app stores, and the old
 * site opened them in a new tab — except the two App Store links, which was an
 * oversight rather than a decision. Spreading these props keeps all of them
 * consistent instead.
 */
const external = { target: "_blank", rel: "noopener" } as const;

/**
 * Screenshots live in `public/img` rather than being imported as modules so the
 * URLs are plain literal strings. The prerenderer renders this component in Node
 * and injects the result into the built HTML; module-imported assets get
 * content-hashed names that would have to match across two separate Vite builds,
 * and a mismatch would silently ship broken <img> tags in the prerendered markup.
 * Literal paths cannot drift.
 *
 * `width`/`height` are the real pixel dimensions of each file. They are what let
 * the browser reserve the right box before the image loads, which is the whole
 * of our cumulative-layout-shift story — so update them whenever a screenshot is
 * replaced (`sips -g pixelWidth -g pixelHeight public/img/*`).
 *
 * Screenshots are displayed under a height cap, so aspect ratio — not file size —
 * decides how large one lands on the page, and a tall capture is shrunk until its
 * UI text stops being readable. `tall` raises that cap from 500px to 640px for the
 * one shot that carries a whole browser frame; see `.feature__img--tall`. Prefer
 * cropping a capture to what the section is actually about over reaching for the
 * flag, because past ~640px the image starts dwarfing the copy beside it.
 */
const FEATURES = [
    {
        // The hero owns the "fencing made easy" tagline now, so this section leads
        // with the concrete capabilities instead of repeating it as an <h2>.
        heading: "Rosters, meets, and live scores in one place",
        body:
            "Touch by Touch is a free service designed for high school and college fencing coaches to manage " +
            "their teams and dual meets. Through the website and companion app, Touch by Touch enables coaches " +
            "and fencers to load team rosters, score bouts, and keep track of how meets are going in real time. " +
            "Live-scored meets are also broadcast publicly for spectators to follow along at home.",
        img: "/img/hs-meet.jpg",
        alt: "A high school dual meet on the Touch by Touch website, showing bout-by-bout scores for both teams",
        width: 1240,
        height: 806,
        tall: false,
    },
    {
        heading: "Let your fencers manage the team",
        body:
            "Touch by Touch streamlines the workflow for the coach as much as possible. As soon as you create " +
            "your team, you can assign a team manager to handle working with Touch by Touch for you, including " +
            "managing your team's roster, creating dual meets, and more.",
        img: "/img/college-editor.jpg",
        // This is the public team page rather than the signed-in roster editor.
        //
        // This one is composited from two bands of the same 900x1300 capture: the
        // nav bar and team title, joined directly to the roster card. The Season
        // Summary card that really sits between them is cut out, because keeping it
        // pushed the image to an aspect ratio that rendered the fencer rows
        // illegibly small. So it is a real screenshot of a real page, minus one
        // card — worth knowing before anyone tries to reproduce it by re-shooting
        // the URL and wonders why theirs looks different.
        alt:
            "A college team roster on the Touch by Touch website, listing every fencer by weapon " +
            "with graduation year, record and rating",
        width: 1240,
        height: 1378,
        tall: true,
    },
    {
        heading: "Score dual meets on the go",
        body:
            "Touch by Touch comes with a fully featured scoring service to make recording meet records easy. You " +
            "can add and remove touches, cards, and time with a simple swipe, as well as substitute fencers and " +
            "set bout priority. Scoring at a dual meet also lets spectators follow along with meet progress in " +
            "real time.",
        img: "/img/live-scorer.jpg",
        // Preserve the entire iPhone screen, including status and navigation.
        // Active dual-meet bout captured in a temporary development meet.
        // The header is retouched to remove AutoScorer and center the dropdown.
        alt: "The full dual meet scorer in the Touch by Touch iPhone app, showing Morris Hills versus Chatham in an active 3–2 epee bout with scoring controls, cards, timeouts, and the bout clock",
        width: 740,
        height: 1609,
        tall: false,
    },
    {
        heading: "Manage on a computer or your phone",
        body:
            "All of Touch by Touch's functionality is accessible through both the website and the mobile app, " +
            "available for Android and iOS. You can type up or upload your roster with the ease of a computer, " +
            "then score dual meets on the go from your phone.",
        // A whole invitational rather than another roster: the section above
        // already shows a roster, and a 24-team, 7-round event says more about
        // what the app carries than a second list of names would.
        img: "/img/college-schedule.jpg",
        alt: "A college invitational's schedule in the Touch by Touch iPhone app, showing each round's meets and final scores",
        width: 740,
        height: 1522,
        tall: false,
    },
] as const;

function App() {
    return (
        <>
            <header className="hero">
                {/* Decorative backdrop: the alt text is empty and it is aria-hidden because
                    the photo carries no information the heading does not already give. */}
                <img
                    className="hero__bg"
                    src="/img/fencers-angled.jpg"
                    alt=""
                    aria-hidden="true"
                    width={2048}
                    height={860}
                    fetchPriority="high"
                />
                <nav className="hero__nav" aria-label="Division sites">
                    {DIVISIONS.map((division) => (
                        <a key={division.key} href={division.website} {...external}>
                            <span>{division.label}</span>
                        </a>
                    ))}
                </nav>
                <h1 className="hero__title">{SITE_NAME}</h1>
                <p className="hero__tagline">{TAGLINE}</p>
            </header>

            <main>
                {FEATURES.map((feature, index) => (
                    <article
                        key={feature.img}
                        /* Odd rows flip the image to the left and take the grey background,
                           producing the alternating zig-zag down the page. */
                        className={`feature${index % 2 === 1 ? " feature--alt" : ""}`}
                    >
                        <div className="feature__text">
                            <h2>{feature.heading}</h2>
                            <p>{feature.body}</p>
                        </div>
                        <img
                            className={`feature__img${feature.tall ? " feature__img--tall" : ""}`}
                            src={feature.img}
                            alt={feature.alt}
                            width={feature.width}
                            height={feature.height}
                            /* The first screenshot is usually above the fold on a laptop;
                               the rest are not worth blocking the initial paint for. */
                            loading={index === 0 ? "eager" : "lazy"}
                            decoding="async"
                        />
                    </article>
                ))}

                <section className="cta" aria-labelledby="get-started">
                    <h2 id="get-started">Use Touch by Touch now</h2>
                    <div className="cta__divisions">
                        {DIVISIONS.map((division) => (
                            <div className="cta__division" key={division.key}>
                                <h3>{division.label}</h3>
                                <a className="cta__button" href={division.website} {...external}>
                                    Visit the website
                                </a>
                                <a className="cta__badge" href={division.android} {...external}>
                                    <img
                                        src="/img/google-play-badge.png"
                                        alt={`Get the ${division.label.toLowerCase()} app on Google Play`}
                                        width={566}
                                        height={170}
                                        loading="lazy"
                                    />
                                </a>
                                <a className="cta__badge" href={division.ios} {...external}>
                                    <img
                                        src="/img/app-store-badge.png"
                                        alt={`Download the ${division.label.toLowerCase()} app on the App Store`}
                                        width={600}
                                        height={201}
                                        loading="lazy"
                                    />
                                </a>
                            </div>
                        ))}
                    </div>
                </section>

                <section className="podcasts" aria-labelledby="podcasts-heading">
                    <div className="podcasts__content">
                        <div>
                            <h2 id="podcasts-heading">Check us out on Substack!</h2>
                            <p>
                                We host the <strong>Fencing Remembers</strong> and{" "}
                                <strong>Beyond the Bout</strong> podcasts.
                            </p>
                        </div>
                        <a className="cta__button podcasts__link" href={SUBSTACK_URL} {...external}>
                            Visit our Substack
                        </a>
                    </div>
                </section>
            </main>

            <footer className="footer">
                <p className="footer__contact">
                    Contact us: <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
                </p>
                <p className="footer__sponsor">Sponsored by {SPONSOR}</p>
            </footer>
        </>
    );
}

export default App;
